import React from 'react';
import './Question.css';
import {xpressApi, GENERATE_BARCODE_DATA, GET_DONOR_PASS_BARCODE} from '../../providers/xpress_api';
import DOMPurify from 'dompurify'
import Header from "../Common/Header/Header";
import Footer from "../Common/Footer/Footer";
import {PATH_QUESTION, PATH_REVIEW, XPRESS_STATE_IS_REVIEW, XPRESS_STATE_RESPONSES, XPRESS_STATE_VIEWED_PAGES} from "../../App";
import {getNextQuestionId, encodeS3URI} from "../../common/common";
import AdobeViewSDKClient from "../../providers/AdobeViewSDKClient";

const DONOR_AID_FILE_TYPE = {
    DOCUMENT: 0,
    IMAGE: 1
}

const DONOR_AID_PDF_DIV_ID = 'donorAidPdfDiv';

export default class Question extends React.Component
{
    constructor(props)
    {
        super(props);
        /**
         * Constructor initializes the following
         * 1. questionnaire -> Global questionnaire data that comes from the questionnaire table
         * 2. questionnaireData ->  All data for each question from question table
         * 3. bloodCenterId ->  Unique identifier of blood center
         * 4. translationId ->  Unique identifier for language of questionnaire
         * 5. firstTimeDonor -> True/False of FTD question response
         * 6. dateOfBirth  ->  From prescreen page
         * 7. gender -> End user sex
         */
        this.warmupLambdaFunctions();

        this.questionId = null;
        this.language = this.props.xpressState.language;
        this.translationConstants = this.props.xpressState.translationConstants;
        this.questionnaire = this.props.xpressState.questionnaire;
        this.bloodCenterProperties = this.props.xpressState.bloodCenterProperties;
        this.bloodCenterId = this.questionnaire.bloodCenterId;
        this.translationId = this.questionnaire.translation;
        this.firstTimeDonor = this.props.xpressState.ftd;
        this.dateOfBirth = this.props.xpressState.dob;
        this.gender = this.props.xpressState.sex;
        this.questionnaireData = this.props.xpressState.questionnaireData.questions;
        this.questionnaireNodeList = this.props.xpressState.questionnaireNodeList;
        this.responseBranchOptions = this.props.xpressState.responseBranchOptions;
        this.responses = this.props.xpressState.responses;
        this.isReview = this.props.xpressState.isReview;
        // Added a list of viewed pages to the Xpress state to keep track of when audio should play (QA-222)
        this.viewedPages = this.props.xpressState.viewedPages;

        this.state = {
            isMobile: false
        };
    }

    componentDidMount() {
        this.handleWindowSizeChange();
        window.addEventListener('resize', this.handleWindowSizeChange);

        this.warmupInterval = setInterval(() => this.warmupLambdaFunctions(), 240000);
        setTimeout(this.clearWarmupInterval, 1800000);

        if (this.state.playEntireAudioFirstDonor && this.state.audioPlaying && this.state.questionAudio) {
            this.setState({disableRadioButtons: true});
        }

        let questionId = this.props.match.params.qId;
        this.initializeQuestionForId(questionId);
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (prevProps.match.params.qId !== this.props.match.params.qId) {
            let questionId = this.props.match.params.qId;
            this.initializeQuestionForId(questionId);
        }
    }

    componentWillUnmount() {
        this.clearWarmupInterval();
        window.removeEventListener('resize', this.handleWindowSizeChange);
    }

    clearWarmupInterval = () => {
        clearInterval(this.warmupInterval);
    }

    warmupLambdaFunctions = () => {
        xpressApi.post(GENERATE_BARCODE_DATA, { noOp: true }).then();
        xpressApi.post(GET_DONOR_PASS_BARCODE, { stage: "noOp" }).then();
    }

    initializeQuestionForId = (questionId) => {
        /*
         * Initializes the question based on the questionId path parameter value.
         *
         * The options (allowable end user choices) are converted from
         * comma separated values to an array.
         */

        if (questionId) {
            this.questionId = questionId;
            let translationId = this.translationId;
            let question = this.questionnaireData.find(questions => questions.questionId === questionId.toString());
            let options = [];
            if (question.questionOptionText !== undefined) {
                options = question.questionOptionText.split(",");
            }

            let questionText = question.questionText;
            let questionImage = question.graphicsFileName;
            let questionAudio = question.audioFileName;
            let questionDonorAidFileName = question.donorAidFileName;
            let questionType = question.questionType;
            let sequence = question.userSequenceCode;
            let playEntireAudioFTD = false; // question.playEntireAudioFirstDonor; - Hardcoded to False since the donor will always be set as a FTD, but we don't want the application to behave as such

            let reviewState = this.responses.find(response => response.questionId.toString() === questionId.toString());

            this.questionDonorAid = questionDonorAidFileName;
            this.questionDonorAidFileType = null;

            if (questionDonorAidFileName) {
                // URL-encode the file name to accommodate special characters (HD #15112)
                questionDonorAidFileName = encodeS3URI(questionDonorAidFileName);
                this.questionDonorAid = questionDonorAidFileName;

                let questionDonorAidFileType = null;
                // Determine the donor aid file type based on the stored MIME type
                switch (question.donorAidFileMimeType) {
                    case 'application/pdf': {
                        questionDonorAidFileType = DONOR_AID_FILE_TYPE.DOCUMENT;
                        break;
                    }
                    case 'image/jpeg':
                    case 'image/pjpeg':
                    case 'image/gif': {
                        questionDonorAidFileType = DONOR_AID_FILE_TYPE.IMAGE;
                        break;
                    }
                }
                this.questionDonorAidFileType = questionDonorAidFileType;

                // Configure and initialize the Adobe PDF Embed API document viewer if the question includes a PDF donor aid
                if (questionDonorAidFileType === DONOR_AID_FILE_TYPE.DOCUMENT) {
                    const adobeViewSDKClient = new AdobeViewSDKClient();
                    adobeViewSDKClient.ready().then(() => {
                        let locale = 'en-US';
                        if(this.language === '2') {
                            locale = 'es-ES';
                        }

                        adobeViewSDKClient.previewFile(DONOR_AID_PDF_DIV_ID, locale, {
                            embedMode: 'IN_LINE',
                            defaultViewMode: 'FIT_WIDTH',
                            //showPageControls: false,
                            //dockPageControls: true,
                            showLeftHandPanel: false,
                            showAnnotationTools: false,
                            enableFormFilling: false,
                            showDownloadPDF: false,
                            showPrintPDF: false,
                            focusOnRendering: false
                        }, {
                            content: {
                                location: {url: questionDonorAidFileName},
                            },
                            metaData: {
                                fileName: 'Donor Aid' // A fileName is required by the Adobe View SDK
                            }
                        });
                    });
                }
            }

            this.setState({
                translationId: translationId,
                questionText: questionText,
                questionType: questionType,
                questionImage: questionImage,
                questionAudio: questionAudio,
                sequence: sequence,
                response: reviewState.response,
                currentQuestionOptions: options,
                loading: false,
                playEntireAudioFirstDonor: playEntireAudioFTD && !this.isReview,
                disableRadioButtons: playEntireAudioFTD === 1 && this.firstTimeDonor && !this.isReview
            });
        }
    }

    /**
     * Block of code below handles the event in which an end user answers a questionnaire
     * question.
     *
     * Each event causes the following:
     *
     * 1. Set the overall state's response variable to the corresponding response
     * 2. Get the index of the responses record that matches questionId of the current state
     * 3. Update this review data record's response value as well
     *
     *
     * DIFFERENCE BETWEEN REVIEW DATA'S RESPONSE VALUE AND STATE'S RESPONSE VALUE
     *
     * --> this.state.response represents the current page's response at any given moment
     * --> responses[index].response represents the response that is always ready to be sent to the review page
     *
     */
    handleOptionChange = async event => {
        let questionResponseIndex;
        let response = 0;

        //
        // find the selected answer in the state's list of answers.  These are ordered correctly, so set the answer to the ordinal index of the matching response.
        //
        for (let i = 0; i < this.state.currentQuestionOptions.length; i++)
        {
            if (event.target.value.toUpperCase() === this.state.currentQuestionOptions[i].toUpperCase())
            {
                response = i + 1;
                questionResponseIndex = this.responses.findIndex(questionResponse => questionResponse.questionId.toString() === this.questionId.toString());
                this.responses[questionResponseIndex].response = response;
                break;
            }
        }
        this.updateXpressStateValue(XPRESS_STATE_RESPONSES, this.responses);

        await this.setState({response: response }); // await is required here because updateVisitedQuestionResponse references this.state.response

        // Navigate to the next question automatically if auto-advance is enabled and we're not in Review
        if (this.bloodCenterProperties.autoAdvance && !this.isReview) {
            await new Promise(resolve => setTimeout(resolve, 175)); // Wait a short period of time to reflect the response selection to the user
            this.getNextQuestion(event);
        }
    }

    getNextQuestion = () => {
        if (this.firstTimeDonor) {
            this.setState({audioPlaying: true});
        }

        // If this is the first time viewing the page, we should add it to the viewed list (QA-222)
        if (this.viewedPages && !this.viewedPages.includes(this.questionId)) {
            this.viewedPages.push(this.questionId);
            this.updateXpressStateValue(XPRESS_STATE_VIEWED_PAGES, this.viewedPages);
        }

        const nextQuestionId = getNextQuestionId(this.questionId, this.state.response, this.questionnaireNodeList, this.responseBranchOptions, this.responses);

        if (!nextQuestionId) {
            // No questions remain, so navigate to the Review page
            this.updateXpressStateValue(XPRESS_STATE_IS_REVIEW, true);
            this.props.history.push(PATH_REVIEW.replace(':bcId', this.bloodCenterId));
        }
        else {
            window.scrollTo(0, 0);
            // Navigate to the next question
            this.props.history.push(PATH_QUESTION.replace(':bcId', this.bloodCenterId).replace(':qId', nextQuestionId));
        }
    }

    getPreviousQuestion = event => {
        const previousQuestionId = this.getPreviousQuestionId();
        if(previousQuestionId) {
            window.scrollTo(0, 0);
            // Navigate to the previous question
            this.props.history.push(PATH_QUESTION.replace(':bcId', this.bloodCenterId).replace(':qId', previousQuestionId));
        }
    }

    goToReviewPage = async event => {
        this.props.history.push(PATH_REVIEW.replace(':bcId', this.bloodCenterId));
    }

    handleWindowSizeChange = () => {
        this.setState({
            isMobile: window.innerWidth < 768, // This is the px breakpoint defined by Bootstrap for small viewports
            audioPlaying: false,
            disableRadioButtons: false
        });
    };

    /** function executed when audio playback ends */
    audioEnded = event => {
        if ((this.firstTimeDonor) && (this.state.playEntireAudioFirstDonor)) {
            this.setState({audioPlaying: false,  disableRadioButtons: false});
        } else {
            this.setState({audioPlaying: false});
        }
    }

    updateXpressStateValue = (key, value) => {
        this.props.fnUpdateXpressStateValue(key, value);
    }

    getPreviousQuestionId = () => {
        let questionnaireNodeList = this.questionnaireNodeList;
        let thisQuestionId = parseInt(this.questionId);

        let currentNodeIndex = questionnaireNodeList.findIndex(node => node.questionId === thisQuestionId);
        let previousNode = questionnaireNodeList[currentNodeIndex - 1];

        // If the previous node is not a child of a parent/branching question (indicated by the node's questionId being equal to its parentQuestionId), return the question ID
        if(previousNode.questionId === previousNode.data.parentQuestionId) {
            return previousNode.questionId;
        }

        // Otherwise, get the topmost parent question of the previous node to determine which previous question to navigate to based on the responses provided, starting from the topmost parent (QA-221)
        do {
            previousNode = questionnaireNodeList.find(node => node.questionId === previousNode.data.parentQuestionId);
        } while (previousNode.questionId !== previousNode.data.parentQuestionId)

        let currentQuestionId = previousNode.questionId;
        let currentResponse = this.responses.find(question => question.questionId === previousNode.questionId.toString()).response;

        let nextQuestionId = null;
        do {
            nextQuestionId = getNextQuestionId(currentQuestionId, currentResponse, questionnaireNodeList, this.responseBranchOptions, this.responses);

            if (nextQuestionId !== thisQuestionId) {
                // The next question's ID based on the response is not that of the question we're navigating backward from, so continue to get the next question
                currentQuestionId = nextQuestionId;
                currentResponse = this.responses.find(question => question.questionId === nextQuestionId.toString()).response;
            }
        } while (nextQuestionId !== thisQuestionId);

        return questionnaireNodeList.find(node => node.questionId === currentQuestionId).questionId;
    }

    render()
    {
        let items = [];
        let image = this.state.questionImage;
        let questionImage;
        let donorAid = null;
        let questionSection;
        let reviewButton;
        let translationConstants = this.translationConstants;
        let previousButton;
        let audio;
        let nextButton;
        let isFirstQuestion = (this.questionId === this.questionnaireData[0].questionId);
        let isMobile = this.state.isMobile;
        let viewedPages = this.viewedPages;

        if (this.state.currentQuestionOptions !== undefined && this.state.currentQuestionOptions.length > 0)
        {
            let keyCount = 1;
            this.state.currentQuestionOptions.forEach(function(element, index)
            {
                items.push(
                    <div key={keyCount} style={{paddingBottom: '10px'}}>
                        <label className="questionOption">
                            <input id={"index" + {...index}} name="questionOptionGroup" type="radio" style={{verticalAlign: 'middle', cursor: 'pointer'}} disabled={this.state.disableRadioButtons === true} checked={this.state.response === keyCount} value={element} onChange={this.handleOptionChange}/>
                            <span style={{marginLeft: '15px', verticalAlign: 'middle', cursor: 'pointer'}}>{element}</span>
                        </label>
                    </div>
                );
                keyCount = keyCount + 1;
            }, this);
        }

        // Audio Player
        // Audio will not automatically start if this question has already been seen (QA-222)
        if (this.state.questionAudio && viewedPages && viewedPages.includes(this.questionId)) {
            audio = <audio controls controlsList="nodownload" src={this.state.questionAudio} style={{width: '250px'}}/>
        } else if (this.state.questionAudio) {
            audio = <audio controls controlsList="nodownload" autoPlay onEnded={this.audioEnded} src={this.state.questionAudio} style={{width: '250px'}}/>
        }

        // Donor Aid
        if (this.questionDonorAid) {
            let donorAidContent;
            if(this.questionDonorAidFileType === DONOR_AID_FILE_TYPE.DOCUMENT) {
                donorAidContent = (<div id={DONOR_AID_PDF_DIV_ID} className="rounded grayBorder" style={{width: '100%'}}/>);
            }
            else if(this.questionDonorAidFileType === DONOR_AID_FILE_TYPE.IMAGE) {
                donorAidContent = (<img className="rounded grayBorder" src={this.questionDonorAid} alt="Donor Aid" style={{maxWidth: '100%'}}/>)
            }

            let reviewDocText = translationConstants.questionnairePageReviewDonorAid + ":";
            donorAid = (
                <div style={{paddingBottom: '1rem'}}>
                    <div><span className="reviewDocument">{reviewDocText}</span></div>
                    <div>{donorAidContent}</div>
                </div>
            );
        }

        // Question Image
        if (image !== undefined && image !== '') {
            questionImage = <div className={(isMobile ? "text-center" : "")}><img className="rounded" src={image} alt="Question" style={{maxWidth: (isMobile ? '125px' : '200px'), maxHeight: (isMobile ? '125px' : '200px'), paddingBottom: (isMobile ? '10px' : '')}}/></div>;
        }
        /* Removing since this creates an unnecessary 200px gap if the question does not contain any response options or audio (HD #15112)
        else {
            if (isMobile) {
                questionImage = undefined;
            } else {
                questionImage = <div style={{width: '200px', height: '200px'}}/>;
            }
        }*/

        // Question section
        questionSection =
            <div className="container">
                <div className="row" style={{paddingBottom: (isMobile ? '' : '0.5em')}}>
                    <div className="col-sm-12 col-md-8">
                        {items.length > 0 && <div>{items}</div>}
                        <div className="text-center text-sm-left">{audio}</div>
                    </div>
                    <div className={isMobile ? '' : 'col-md-4 text-center'}>{!isMobile && questionImage}</div>
                </div>
            </div>;

        // Previous Button (only displayed if not the first question)
        if (isFirstQuestion) {
            previousButton = null;
        }
        else {
            previousButton = <button className="btn btn-secondary btn-lg questionNavButton" onClick={this.getPreviousQuestion}>{translationConstants.questionnairePagePrevious}</button>;
        }

        // Next Button
        nextButton = <button className="btn btn-success btn-lg questionNavButton" disabled={this.state.disableRadioButtons === true} onClick={this.getNextQuestion}>{translationConstants.questionnairePageNext}</button>;

        // Review Button
        if(this.isReview) {
            reviewButton = <button id="reviewButton" className="btn btn-warning btn-lg questionNavButton" onClick={this.goToReviewPage}>{translationConstants.questionnairePageReview}</button>;
        }
        else {
            reviewButton = null;
        }

        return (
            <div className="pageWrapper">
                <div className="contentWrapper" style={{width: '100%'}}>
                    <Header logoPath={this.questionnaire.logoPath} taglinePath={this.questionnaire.taglinePath}/>
                    {isMobile && questionImage} {/* Display the question image at the top of the page rather than next to the question when using a mobile device */}
                    <div className="container">
                        <div className="row" style={{maxWidth: (isMobile ? '' : '900px'), marginLeft: (isMobile ? '' : 'auto'), marginRight: (isMobile ? '': 'auto')}}>
                            <div className="col-md-12">
                                <div className="card">
                                    <div className="card-body">
                                        <h4 className="card-title" style={{color: 'gray'}}>{this.state.sequence}</h4>
                                        <h4>
                                            <div className="card-text" dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(this.state.questionText)}}/>
                                        </h4>
                                        <hr/>
                                        {donorAid}
                                        {questionSection}
                                        <div className="container" style={{paddingTop: '10px'}}>
                                            <div className="row justify-content-center">
                                                <div className={!isFirstQuestion ? 'col-xs-4' : ''}>
                                                    {previousButton}
                                                    {/* Add vertical padding between the buttons if in Review and an XS viewport is being used, which causes them to wrap */}
                                                    {this.isReview && <div className="d-block d-sm-none" style={{paddingBottom: '10px'}}/>}
                                                </div>
                                                <div className="col-xs-4">
                                                    {nextButton}
                                                </div>
                                                <div className={this.isReview ? 'col-xs-4' : ''}>
                                                    {reviewButton}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer text={translationConstants.xpressPoweredBy}/>
            </div>
        );
    }
}
